import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UUID } from '@annote/types';
import { PANEL_WIDTHS_PX } from 'src/constants/panelSizes';

export type PanelId = 'lib' | 'reader' | 'writer' | 'profile';

export interface PanelState {
  isOpen: boolean;
  activeId?: UUID;
  width?: number; // Panel width in pixels
  isPinned?: boolean; // Whether panel stays open (only for library)
}

export interface ViewportBounds {
  x: number; // Left edge of viewport (after left panels)
  y: number; // Top edge of viewport (always 0 for now)
  width: number; // Available width (after subtracting panels)
  height: number; // Available height (full window height for now)
}

export interface PanelContextValue {
  panels: Record<PanelId, PanelState>;
  openPanel: (panelId: PanelId, activeId?: UUID) => void;
  closePanel: (panelId: PanelId) => void;
  togglePanel: (panelId: PanelId) => void;
  setActiveId: (panelId: PanelId, activeId: UUID) => void;
  setPanelWidth: (panelId: PanelId, width: number) => void;
  getPanelWidth: (panelId: PanelId) => number | undefined;
  togglePinned: (panelId: PanelId) => void; // Toggle pinned state for panel
  leftPanelsWidth: number; // Aggregate width of left panels (lib + reader)
  rightPanelsWidth: number; // Aggregate width of right panels (writer)
  viewport: ViewportBounds; // Available viewport after panel adjustments
}

const PanelContext = createContext<PanelContextValue | undefined>(undefined);

export const usePanelContext = (): PanelContextValue => {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanelContext must be used within PanelProvider');
  }
  return context;
};

interface PanelProviderProps {
  children: React.ReactNode;
}

export const PanelProvider: React.FC<PanelProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Track window dimensions for viewport calculations
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Initialize panel states from URL or defaults
  const [panels, setPanels] = useState<Record<PanelId, PanelState>>(() => {
    const libOpen = searchParams.has('lib');
    const readerParam = searchParams.get('reader');
    const writerParam = searchParams.get('writer');
    const profileOpen = searchParams.has('profile');

    return {
      lib: { isOpen: libOpen || !readerParam }, // Default: open, but close when reader is open
      reader: { isOpen: !!readerParam, activeId: readerParam || undefined },
      writer: { isOpen: !!writerParam, activeId: writerParam || undefined },
      profile: { isOpen: profileOpen },
    };
  });

  // Track window resize for viewport calculations
  useEffect(() => {
    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync panel state to URL
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    // Library
    if (panels.lib.isOpen) {
      newParams.set('lib', '');
    } else {
      newParams.delete('lib');
    }

    // Reader
    if (panels.reader.isOpen && panels.reader.activeId) {
      newParams.set('reader', panels.reader.activeId);
    } else {
      newParams.delete('reader');
    }

    // Writer
    if (panels.writer.isOpen && panels.writer.activeId) {
      newParams.set('writer', panels.writer.activeId);
    } else {
      newParams.delete('writer');
    }

    // Profile
    if (panels.profile.isOpen) {
      newParams.set('profile', '');
    } else {
      newParams.delete('profile');
    }

    setSearchParams(newParams, { replace: true });
  }, [panels, setSearchParams]);

  const openPanel = useCallback((panelId: PanelId, activeId?: UUID): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        isOpen: true,
        activeId: activeId || prev[panelId].activeId,
      },
    }));
  }, []);

  const closePanel = useCallback((panelId: PanelId): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        ...prev[panelId],
        isOpen: false,
      },
    }));
  }, []);

  const togglePanel = useCallback((panelId: PanelId): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        ...prev[panelId],
        isOpen: !prev[panelId].isOpen,
      },
    }));
  }, []);

  const togglePinned = useCallback((panelId: PanelId): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        ...prev[panelId],
        isPinned: !prev[panelId].isPinned,
        // When pinning, also open the panel
        isOpen: !prev[panelId].isPinned ? true : prev[panelId].isOpen,
      },
    }));
  }, []);

  const setActiveId = useCallback((panelId: PanelId, activeId: UUID): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        ...prev[panelId],
        activeId,
      },
    }));
  }, []);

  const setPanelWidth = useCallback((panelId: PanelId, width: number): void => {
    setPanels((prev) => ({
      ...prev,
      [panelId]: {
        ...prev[panelId],
        width,
      },
    }));
  }, []);

  const getPanelWidth = useCallback(
    (panelId: PanelId): number | undefined => {
      return panels[panelId]?.width;
    },
    [panels],
  );

  // Calculate aggregate panel widths for left and right sides
  // LEFT: Library + Reader (stacked on left side)
  // RIGHT: Writer only
  // Note: Reader stays to the right of library and slides with it, but is independently toggleable
  // Note: Use fallback defaults from PANEL_WIDTHS_PX constants
  const leftPanelsWidth = useMemo((): number => {
    const libWidth = panels.lib.isOpen ? panels.lib.width || PANEL_WIDTHS_PX.LIBRARY : 0;
    const readerWidth = panels.reader.isOpen ? panels.reader.width || PANEL_WIDTHS_PX.READER : 0;
    return libWidth + readerWidth;
  }, [panels.lib.isOpen, panels.lib.width, panels.reader.isOpen, panels.reader.width]);

  const rightPanelsWidth = useMemo((): number => {
    return panels.writer.isOpen ? panels.writer.width || PANEL_WIDTHS_PX.WRITER : 0;
  }, [panels.writer.isOpen, panels.writer.width]);

  // Calculate viewport bounds after subtracting panel widths
  // This is the raw available space - surfaces can apply their own padding
  const viewport = useMemo((): ViewportBounds => {
    const availableWidth = windowSize.width - leftPanelsWidth - rightPanelsWidth;
    return {
      x: leftPanelsWidth,
      y: 0,
      width: availableWidth,
      height: windowSize.height,
    };
  }, [windowSize, leftPanelsWidth, rightPanelsWidth]);

  const value = useMemo(
    (): PanelContextValue => ({
      panels,
      openPanel,
      closePanel,
      togglePanel,
      setActiveId,
      setPanelWidth,
      getPanelWidth,
      togglePinned,
      leftPanelsWidth,
      rightPanelsWidth,
      viewport,
    }),
    [
      panels,
      openPanel,
      closePanel,
      togglePanel,
      setActiveId,
      setPanelWidth,
      getPanelWidth,
      togglePinned,
      leftPanelsWidth,
      rightPanelsWidth,
      viewport,
    ],
  );

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
};
